import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useOffline } from '../contexts/OfflineContext';
import { ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { PRODUCTOS } from '../constants/productos';
import { fmtMoney } from '../utils/money';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MiJornada() {
  const { user } = useAuth();
  const { cacheData, getCachedData } = useOffline();
  const navigate = useNavigate();
  
  const [puntoVenta, setPuntoVenta] = useState(null);
  const [precios, setPrecios] = useState({});
  const [pesoProm, setPesoProm] = useState(0);
  const [asistencia, setAsistencia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllPrecios, setShowAllPrecios] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Check attendance
      const { data: asistenciaData } = await axios.get(`${API}/asistencia/hoy`);
      setAsistencia(asistenciaData);
      
      // Get puntos de venta
      const { data: puntos } = await axios.get(`${API}/puntos-venta`);
      cacheData('puntos-venta', puntos);
      
      // If user has assigned punto or asistencia registered
      const puntoId = asistenciaData?.punto_venta_id || user?.punto_venta_id;
      if (puntoId) {
        const punto = puntos.find(p => p.id === puntoId);
        setPuntoVenta(punto);
        
        // Get prices
        const { data: preciosData } = await axios.get(`${API}/precios/${puntoId}`);
        setPrecios(preciosData.precios || {});
        setPesoProm(preciosData.peso_promedio || 0);
        cacheData(`precios_${puntoId}`, preciosData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 18) return 'Buenas tardes';
    return 'Buenas noches';
  };

  const fechaHoy = new Date().toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });

  useEffect(() => {
    if (!loading && asistencia) {
      navigate('/pos');
    }
  }, [loading, asistencia, navigate]);

  if (loading || asistencia) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <span className="inline-block w-8 h-8 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
      </div>
    );
  }

  const preciosToShow = showAllPrecios ? PRODUCTOS : PRODUCTOS.slice(0, 5);
  const remainingCount = PRODUCTOS.length - 5;

  return (
    <div className="min-h-screen bg-white" data-testid="mi-jornada-page">
      <div className="px-6 py-8">
        {/* Header */}
        <div className="border-b border-zinc-200 pb-4 mb-6">
          <h1 className="text-2xl font-bold text-zinc-900">Mi jornada</h1>
        </div>

        {/* Greeting */}
        <div className="mb-6">
          <p className="text-zinc-500 text-lg">{getGreeting()},</p>
          <h2 className="text-3xl font-bold text-zinc-900">{user?.name}</h2>
        </div>

        {/* Punto de venta asignado */}
        {puntoVenta ? (
          <div className="bg-zinc-100 rounded-xl p-4 mb-8">
            <p className="text-zinc-500 text-sm mb-1">Punto de venta asignado</p>
            <p className="text-xl font-bold text-zinc-900">{puntoVenta.name}</p>
            {puntoVenta.address && (
              <p className="text-zinc-600">{puntoVenta.address}</p>
            )}
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-8">
            <p className="text-yellow-800">No tienes un punto de venta asignado</p>
          </div>
        )}

        {/* Precios del día */}
        <div className="mb-8">
          <h3 className="text-zinc-500 text-sm font-medium tracking-wide mb-4">
            PRECIOS DEL DÍA {pesoProm > 0 && `(factor ${pesoProm} kg)`}
          </h3>
          
          <div className="space-y-3">
            {preciosToShow.map((producto) => {
              const precio = precios[producto.clave] || 0;
              return (
                <div key={producto.clave} className="flex items-center justify-between">
                  <span className="text-lg font-medium text-zinc-900">{producto.nombre}</span>
                  <div className="text-right">
                    <span className="text-2xl font-bold text-zinc-900">
                      {precio > 0 ? fmtMoney(precio) : '--'}
                    </span>
                    <span className="text-zinc-500 text-sm"> / pieza</span>
                  </div>
                </div>
              );
            })}
          </div>

          {!showAllPrecios && remainingCount > 0 && (
            <button
              onClick={() => setShowAllPrecios(true)}
              className="mt-4 text-zinc-500 text-sm flex items-center gap-1"
              data-testid="btn-show-more-precios"
            >
              + {remainingCount} productos más
              <ChevronDown className="w-4 h-4" />
            </button>
          )}
          {showAllPrecios && (
            <button
              onClick={() => setShowAllPrecios(false)}
              className="mt-4 text-zinc-500 text-sm flex items-center gap-1"
            >
              Ver menos
              <ChevronUp className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Registrar asistencia button */}
        <button
          onClick={() => navigate('/asistencia')}
          className="w-full min-h-14 bg-white text-zinc-900 border border-zinc-300 rounded-xl px-4 py-3 flex items-center justify-between gap-2 hover:bg-zinc-50 active:bg-zinc-100"
          data-testid="btn-registrar-asistencia"
        >
          <div className="text-left">
            <p className="text-lg font-medium leading-tight">Registrar asistencia</p>
            <p className="text-sm text-zinc-500 capitalize">{fechaHoy}</p>
          </div>
          <ChevronRight className="w-5 h-5 shrink-0" />
        </button>
      </div>
    </div>
  );
}
